import { api } from './client';
import type { FormSchema } from '@/domain/schemaForm';

export interface LinkInfo {
  title: string;
  organizationName: string;
  requireEmailVerification: boolean;
  /** 掩码后的收件邮箱，如 `zh****@lessie.ai`。只够本人认出是哪个信箱。 */
  emailHint: string | null;
  /** 创建人预设了币种的话，填写人不用也不能再选。 */
  currency: string | null;
  status: 'PENDING' | 'FILLED' | 'REVOKED';
  expiresAt: string;
}

export async function getInfo(token: string) {
  const { data } = await api.post<LinkInfo>('/collect/info', { token });
  return data;
}

/**
 * 发验证码。
 * 🔴 **不传邮箱**：收件地址由创建链接的人绑定，填写人改不了（服务端也不接受）。
 */
export async function sendCode(token: string) {
  const { data } = await api.post<{ emailHint: string }>('/collect/code/send', { token });
  return data.emailHint;
}

export interface CollectSessionGranted {
  collectToken: string;
  /**
   * 令牌到期时刻（ISO）。🔴 界面据此**提前**提示与退回验证 ——
   * 不给这个值的话，填写人只能在点「提交」那一刻才知道自己超时了，一屏银行信息白填。
   */
  expiresAt: string;
}

export async function verifyCode(token: string, code: string) {
  const { data } = await api.post<CollectSessionGranted>('/collect/code/verify', { token, code });
  return data;
}

export interface SchemaQuery {
  bank_country_code?: string;
  account_currency?: string;
  transfer_method?: string;
  entity_type?: string;
  local_clearing_system?: string;
  country_code?: string;
}

export async function getSchema(token: string, collectToken: string | null, query: SchemaQuery) {
  const { data } = await api.post<FormSchema>('/collect/schema', {
    token,
    ...(collectToken ? { collectToken } : {}),
    ...query,
  });
  return data;
}

export async function searchInstitutions(
  token: string,
  collectToken: string | null,
  query: {
    keyword: string;
    bank_country_code: string;
    account_currency: string;
    transfer_method: string;
    entity_type: string;
    local_clearing_system?: string;
  },
) {
  const { data } = await api.post<{ items: Array<{ label: string; value: string }> }>(
    '/collect/institutions',
    { token, ...(collectToken ? { collectToken } : {}), ...query },
  );
  return data;
}

export async function validate(
  token: string,
  collectToken: string | null,
  values: Record<string, string>,
) {
  const { data } = await api.post<{ ok: true }>('/collect/validate', {
    token,
    ...(collectToken ? { collectToken } : {}),
    values,
  });
  return data;
}

export async function submit(
  token: string,
  collectToken: string | null,
  values: Record<string, string>,
) {
  const { data } = await api.post<{ ok: true }>('/collect/submit', {
    token,
    ...(collectToken ? { collectToken } : {}),
    values,
  });
  return data;
}
